import React, { useState, useEffect, useRef } from 'react';
import { FaBell, FaTimes, FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import notificationService, { type RealtimeNotification, type NotificationStats } from '../services/NotificationService';
import { apiRequest, API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface NotificationBellProps {
  compact?: boolean;
}

export default function NotificationBell({ compact = false }: NotificationBellProps) {
  const { t } = useTranslation(['notifications']);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState<RealtimeNotification[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fetchGenerationRef = useRef(0);
  const navigate = useNavigate();

  // Subscribe to stats updates and initialize stats
  useEffect(() => {
    // Subscribe to stats updates
    const unsubscribe = notificationService.addStatsListener((stats: NotificationStats) => {
      setUnreadCount(stats.unread || 0);
    });
    
    // Always refresh stats on mount to ensure we have the latest count from backend
    // This ensures the badge shows the correct count even if SSE connection isn't established yet
    notificationService.refreshStats().catch(err => {
      console.error('NotificationBell: Error refreshing stats:', err);
      // Fallback to cached stats if refresh fails
      const currentStats = notificationService.getStats();
      setUnreadCount(currentStats.unread || 0);
    });

    const statsPoll = window.setInterval(() => {
      if (!notificationService.isConnected()) {
        notificationService.refreshStats().catch(err => {
          console.error('NotificationBell: Error polling stats:', err);
        });
      }
    }, 30000);
    
    return () => {
      window.clearInterval(statsPoll);
      unsubscribe();
    };
  }, []);

  // Subscribe to new notifications
  useEffect(() => {
    const unsubscribe = notificationService.addListener((notification: RealtimeNotification) => {
      // Show toast for new notification
      toast.success(notification.title, {
        duration: 4000,
        position: 'top-center',
      });

      // Update recent notifications if dropdown is open
      if (showDropdown) {
        setRecentNotifications(prev => [notification, ...prev.slice(0, 4)]);
      }
      
      // Refresh stats to ensure badge counter is updated
      // The service should have updated stats automatically, but refresh to be sure
      notificationService.refreshStats().catch(err => {
        console.error('NotificationBell: Error refreshing stats after notification:', err);
      });
    });
    return unsubscribe;
  }, [showDropdown]);

  // Fetch recent notifications when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      fetchRecentNotifications();
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const fetchRecentNotifications = async () => {
    const generation = ++fetchGenerationRef.current;
    try {
      setListLoading(true);
      const response = await apiRequest<{ notifications: RealtimeNotification[] }>(
        `${API_ENDPOINTS.NOTIFICATIONS}?limit=8&page=1`
      );
      if (generation !== fetchGenerationRef.current) return;
      setRecentNotifications(response.notifications || []);
    } catch (error) {
      if (generation !== fetchGenerationRef.current) return;
      console.error('Error fetching notifications:', error);
      toast.error(t('notifications:messages.fetchFailed'));
    } finally {
      if (generation === fetchGenerationRef.current) {
        setListLoading(false);
      }
    }
  };

  const applyStats = (stats?: NotificationStats | null) => {
    if (stats) {
      notificationService.applyServerStats(stats);
      return;
    }
    notificationService.refreshStats().catch(() => undefined);
  };

  const handleNotificationClick = async (notification: RealtimeNotification) => {
    try {
      if (!notification.read) {
        setRecentNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        notificationService.optimisticMarkRead(1);
        const result = await apiRequest<{ stats?: NotificationStats }>(
          API_ENDPOINTS.NOTIFICATIONS_MARK_READ.replace(':id', notification.id.toString()),
          'PUT'
        );
        applyStats(result.stats);
      }

      const navigationPath = notification.data?.navigationPath || '/notifications';
      setShowDropdown(false);
      navigate(navigationPath);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      notificationService.refreshStats().catch(() => undefined);
    }
  };

  const handleMarkOneRead = async (event: React.MouseEvent, notification: RealtimeNotification) => {
    event.preventDefault();
    event.stopPropagation();
    if (notification.read) return;

    setRecentNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
    notificationService.optimisticMarkRead(1);

    try {
      const result = await apiRequest<{ stats?: NotificationStats }>(
        API_ENDPOINTS.NOTIFICATIONS_MARK_READ.replace(':id', notification.id.toString()),
        'PUT'
      );
      applyStats(result.stats);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setRecentNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, read: false } : n)
      );
      notificationService.refreshStats().catch(() => undefined);
      toast.error(t('notifications:messages.markReadFailed'));
    }
  };

  const handleMarkAllRead = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (unreadCount === 0 || markingAll) return;

    const previous = recentNotifications;
    fetchGenerationRef.current += 1;
    setListLoading(false);
    setRecentNotifications(prev => prev.map(n => ({ ...n, read: true })));
    notificationService.optimisticMarkAllRead();
    setMarkingAll(true);

    try {
      const result = await apiRequest<{ stats?: NotificationStats }>(
        API_ENDPOINTS.NOTIFICATIONS_MARK_ALL_READ,
        'PUT'
      );
      applyStats(result.stats);
      toast.success(t('notifications:messages.allMarkedRead'));
    } catch (error) {
      console.error('Error marking all as read:', error);
      setRecentNotifications(previous);
      toast.error(t('notifications:messages.markAllFailed'));
      notificationService.refreshStats().catch(() => undefined);
    } finally {
      setMarkingAll(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications:time.justNow');
    if (diffMins < 60) return `${diffMins}${t('notifications:time.minutesAgo_short')}`;
    if (diffHours < 24) return `${diffHours}${t('notifications:time.hoursAgo_short')}`;
    if (diffDays < 7) return `${diffDays}${t('notifications:time.daysAgo_short')}`;
    return date.toLocaleDateString();
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'content': return 'bg-blue-100 text-blue-600';
      case 'operations': return 'bg-green-100 text-green-600';
      case 'admin': return 'bg-purple-100 text-purple-600';
      case 'communication': return 'bg-orange-100 text-orange-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={
          compact
            ? 'relative h-7 w-7 flex items-center justify-center rounded-[8px] text-[#6D6D72] active:bg-white/80 transition-colors focus:outline-none touch-manipulation'
            : 'relative p-2 text-gray-700 hover:text-orange-600 transition-colors focus:outline-none'
        }
        aria-label={t('notifications:dropdown.ariaLabel')}
      >
        <FaBell size={compact ? 14 : 20} />
        {unreadCount > 0 && (
          <span
            className={`absolute bg-[#FF3B30] text-white font-bold rounded-full flex items-center justify-center ${
              compact
                ? 'top-0 right-0 min-w-[14px] h-3.5 text-[9px] px-0.5'
                : '-top-1 -right-1 min-w-[20px] h-5 text-xs px-1'
            }`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 sm:w-[22rem] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">{t('notifications:dropdown.header')}</h3>
              {unreadCount > 0 && (
                <span className="min-w-[1.25rem] h-4 px-1 rounded-full bg-orange-600 text-white text-[10px] font-semibold flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                  className="px-2 py-0.5 text-xs font-medium text-orange-600 hover:bg-orange-50 rounded-md disabled:opacity-50"
                  title={t('notifications:dropdown.markAllReadTooltip')}
                >
                  <FaCheck className="inline mr-1" />
                  {t('notifications:dropdown.markAllRead')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowDropdown(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-md"
              >
                <FaTimes size={12} />
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {listLoading && recentNotifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mx-auto"></div>
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <FaBell size={28} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">{t('notifications:dropdown.noNotifications')}</p>
                <p className="text-xs">{t('notifications:allCaughtUp')}</p>
              </div>
            ) : (
              recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-stretch border-b border-gray-100 last:border-b-0 ${
                    !notification.read ? 'bg-orange-50' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleNotificationClick(notification)}
                    className="flex-1 min-w-0 text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      {!notification.read ? (
                        <span className="flex-shrink-0 w-1.5 h-1.5 bg-orange-600 rounded-full mt-1.5"></span>
                      ) : (
                        <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5"></span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-5 ${!notification.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                            {notification.title}
                          </p>
                          <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${getCategoryColor(notification.category)}`}>
                            {notification.category}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {formatTimestamp(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </button>
                  {!notification.read && (
                    <button
                      type="button"
                      onClick={(event) => handleMarkOneRead(event, notification)}
                      className="px-2 self-stretch text-green-600 hover:bg-green-50"
                      title={t('notifications:markAsRead')}
                    >
                      <FaCheck size={11} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              type="button"
              onClick={() => {
                setShowDropdown(false);
                navigate('/notifications');
              }}
              className="w-full text-center text-xs text-orange-600 hover:text-orange-700 font-medium"
            >
              {t('notifications:dropdown.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


